import React from 'react';
import { TreeItem } from '@mui/x-tree-view';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { greenCheckIcon, redCrossIcon } from '../assets';
import { ITestCase } from '../types/interfaces/testCase.interface';
import { buildNodeKey } from '../utils/nodeId';
import { useNavigate } from 'react-router-dom';
import TreeItemLabel from './TreeItemLabel';
import { formatTimestamp } from '../helpers/date.helper';

interface TestCaseTreeItemProps {
  testCase: ITestCase;
  parentProjectId: number;
  onContextMenu: (
    event: React.MouseEvent,
    nodeId: string,
    nodeType: 'testcase',
    parentId: number
  ) => void;
  onSelectedItemsChange: (event: React.SyntheticEvent | null, itemIds: string[] | string) => void;
  showModifiedDates: boolean;
  filterText: string;
}

export const TestCaseTreeItem: React.FC<TestCaseTreeItemProps> = ({
  testCase,
  parentProjectId,
  onContextMenu,
  onSelectedItemsChange,
  showModifiedDates,
  filterText,
}) => {
  const navigate = useNavigate();

  if (typeof testCase.testCaseId !== 'number') {
    console.warn('Skipping test case with missing/invalid ID:', testCase);
    return null;
  }

  const itemId = buildNodeKey({ type: 'testcase', id: testCase.testCaseId });
  const label = `${testCase.testCaseId} - ${testCase.name || 'Test Case'}`;
  const subText =
    showModifiedDates && testCase.lastModifiedTime
      ? formatTimestamp(testCase.lastModifiedTime)
      : undefined;

  // Determine last run status icon
  let statusIcon: React.ReactNode = null;
  const lastRun = (testCase as any)?.lastTestRun;
  if (lastRun) {
    if (
      lastRun === 'RUNNING' ||
      (Array.isArray(lastRun) && lastRun[0]?.status === 'RUNNING')
    ) {
      statusIcon = <PendingOutlinedIcon style={{ fill: 'blue', fontSize: 16 }} />;
    } else {
      const pass =
        lastRun === 'PASS' || (Array.isArray(lastRun) && lastRun[0]?.status === 'PASS');
      statusIcon = (
        <img
          src={pass ? greenCheckIcon : redCrossIcon}
          alt=""
          style={{ width: 16, height: 16 }}
        />
      );
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    onSelectedItemsChange(event, itemId);

    navigate(`/folders/project/${parentProjectId}/testcase/${testCase.testCaseId}`);
  };

  return (
    <TreeItem
      key={itemId}
      itemId={itemId}
      slots={{ icon: ChevronRightIcon }}
      label={<TreeItemLabel text={label} statusIcon={statusIcon} subText={subText} searchTerm={filterText} />}
      slotProps={{
        icon: { fontSize: 'medium', color: 'action' },
      }}
      onContextMenu={(e) => onContextMenu(e, itemId, 'testcase', parentProjectId)}
      onClick={handleClick}
    />
  );
}; 